import React, { useCallback, useEffect, useState } from "react";
import { useLanguage } from "../../contexts/LanguageContext";
import { useTheme } from "../../contexts/ThemeContext";
import { getApiUrl } from "../../config/api";
import { useAuth } from "../../contexts/AuthContext";
import AIChatWidget from "./AIChatWidget";
import CompanionHighlightOverlay from "./CompanionHighlightOverlay";
import { guardArabicCompanionCopy } from "../../utils/mmecOptionsGlossary";
import type { CompanionStatePayload } from "../../utils/companionSync";
import {
    applyCompanionHighlight,
    clearCompanionHighlights,
} from "../../utils/companionHighlight";
import { useTour } from "../../contexts/TourContext";
import { isDesignSystemOn } from "../../theme/mmecDsPreview";
import { ELITE_PULSE_NEW_REPORT_EVENT } from "../../utils/elitePulseReportNotify";
import {
    COMPANION_POPUP_SETTINGS_CHANGED,
    loadCompanionPopupSettings,
    saveCompanionPopupSettings,
} from "../../utils/companionPopupSettingsStore";

const POLL_MS = 45_000;

export interface MMECCompanionProps {
    isCollapsed?: boolean;
    onToggle?: () => void;
    marketContext?: Record<string, unknown>;
}

const MMECCompanion: React.FC<MMECCompanionProps> = ({
    isCollapsed = false,
    onToggle,
    marketContext = {},
}) => {
    const { language } = useLanguage();
    const { colors } = useTheme();
    const { token } = useAuth();
    const ar = language === "ar";
    const { startTour, isTourActive } = useTour();

    const [companion, setCompanion] = useState<
        CompanionStatePayload["companion"]
    >({});
    const [serverState, setServerState] =
        useState<CompanionStatePayload | null>(null);
    const [popupVisible, setPopupVisible] = useState(false);
    const [popupsEnabled, setPopupsEnabled] = useState(
        () => loadCompanionPopupSettings().popupsEnabled,
    );
    const [chatCollapsed, setChatCollapsed] = useState(isCollapsed);

    useEffect(() => {
        const sync = () =>
            setPopupsEnabled(loadCompanionPopupSettings().popupsEnabled);
        window.addEventListener(COMPANION_POPUP_SETTINGS_CHANGED, sync);
        return () =>
            window.removeEventListener(COMPANION_POPUP_SETTINGS_CHANGED, sync);
    }, []);

    const togglePopups = useCallback(() => {
        const next = !popupsEnabled;
        setPopupsEnabled(next);
        saveCompanionPopupSettings({ popupsEnabled: next });
        if (!next) setPopupVisible(false);
    }, [popupsEnabled]);

    // On mobile, default to collapsed so the chatbot doesn't overtake the screen
    const isMobile = typeof window !== "undefined" && window.innerWidth < 768;
    useEffect(() => {
        if (isMobile && !localStorage.getItem("mmec_ai_chat_collapsed")) {
            setChatCollapsed(true);
        }
    }, [isMobile]);

    const effectiveChatCollapsed =
        onToggle !== undefined ? isCollapsed : chatCollapsed;
    const chatOpen = !effectiveChatCollapsed;

    const openChat = useCallback(() => {
        if (onToggle && effectiveChatCollapsed) onToggle();
        else if (!onToggle) setChatCollapsed(false);
    }, [onToggle, effectiveChatCollapsed]);

    const closeChat = useCallback(() => {
        clearCompanionHighlights();
        if (onToggle && !effectiveChatCollapsed) onToggle();
        else if (!onToggle) setChatCollapsed(true);
    }, [onToggle, effectiveChatCollapsed]);

    const fetchState = useCallback(async () => {
        try {
            const headers: Record<string, string> = {
                Accept: "application/json",
            };
            if (token) headers.Authorization = `Bearer ${token}`;
            const res = await fetch(getApiUrl("/api/companion/state"), {
                headers,
            });
            if (res.status === 429) return;
            if (!res.ok) return;
            const data = await res.json();
            if (!data?.success || !data.state) return;
            const st = data.state as CompanionStatePayload;
            setServerState(st);
            const comp = st.companion || {};
            setCompanion(comp);
            if (comp.show_popup && (comp.message_ar || comp.message_en) && popupsEnabled) {
                setPopupVisible(true);
            }
        } catch {
            /* ignore */
        }
    }, [token, popupsEnabled]);

    useEffect(() => {
        void fetchState();
        const id = window.setInterval(() => void fetchState(), POLL_MS);
        return () => clearInterval(id);
    }, [fetchState]);

    useEffect(() => {
        const onPulse = (ev: Event) => {
            const detail = (ev as CustomEvent<{ openChat?: boolean }>).detail;
            if (detail?.openChat) openChat();
        };
        window.addEventListener(ELITE_PULSE_NEW_REPORT_EVENT, onPulse);
        return () =>
            window.removeEventListener(ELITE_PULSE_NEW_REPORT_EVENT, onPulse);
    }, [openChat]);

    const ackPopup = async () => {
        setPopupVisible(false);
        try {
            const headers: Record<string, string> = {
                "Content-Type": "application/json",
            };
            if (token) headers.Authorization = `Bearer ${token}`;
            await fetch(getApiUrl("/api/companion/ack-popup"), {
                method: "POST",
                headers,
            });
        } catch {
            /* ignore */
        }
    };

    const openExpanded = () => {
        setPopupVisible(false);
        void ackPopup();
        openChat();
    };

    const goElitePulse = () => {
        const simple = document.querySelector(".mmec-simple-shell") != null;
        if (simple) {
            window.dispatchEvent(
                new CustomEvent("mmec-simple-navigate-tab", {
                    detail: { tab: "elite_pulse" },
                }),
            );
        } else {
            window.dispatchEvent(
                new CustomEvent("navigate-to-tab", {
                    detail: { tab: "elite-pulse" },
                }),
            );
        }
        openExpanded();
    };

    const message = guardArabicCompanionCopy(
        companion?.message_ar?.trim() ||
            companion?.message_en?.trim() ||
            "",
        "راجع تقرير نبض النخبة أو اسأل عن رمز من الفرص المعروضة.",
    );
    /** Companion popups and banner default to Arabic. */
    const companionCopyAr = true;
    const highlights = (companion?.highlights || []).filter(
        (h): h is { symbol: string; headline_ar?: string } =>
            Boolean(h?.symbol),
    );

    const spotlightSymbol = (symbol: string, headline?: string) => {
        applyCompanionHighlight({
            symbol,
            message:
                headline || `ركز على ${symbol}`,
        });
    };

    return (
        <>
            <CompanionHighlightOverlay />

            {popupVisible && popupsEnabled && message ? (
                <div
                    role="dialog"
                    aria-live="assertive"
                    style={{
                        position: "fixed",
                        inset: 0,
                        zIndex: 10002,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        padding: 16,
                        backgroundColor: "rgba(0,0,0,0.45)",
                    }}
                    onClick={() => void ackPopup()}
                >
                    <div
                        onClick={(e) => e.stopPropagation()}
                        style={{
                            maxWidth: 440,
                            width: "100%",
                            borderRadius: 12,
                            overflow: "hidden",
                            border: "1px solid var(--border)",
                            backgroundColor: "var(--elev)",
                            boxShadow: "var(--lift-3)",
                        }}
                    >
                        <div
                            style={{
                                display: "flex",
                                gap: 12,
                                padding: 16,
                                background: `linear-gradient(135deg, ${colors.primary}22, ${colors.primary}08)`,
                                borderBottom: "1px solid var(--hair)",
                            }}
                        >
                            <CompanionAvatar size={56} />
                            <div style={{ flex: 1, minWidth: 0 }}>
                                <p
                                    style={{
                                        margin: 0,
                                        fontSize: 10.5,
                                        fontWeight: 600,
                                        textTransform: "uppercase",
                                        letterSpacing: ".08em",
                                        color: "var(--muted-2)",
                                    }}
                                >
                                    {companionCopyAr ? "مرافق MMEC" : "MMEC Companion"}
                                </p>
                                <p
                                    style={{
                                        margin: "4px 0 0",
                                        fontSize: 14,
                                        fontWeight: 600,
                                        color: "var(--txt)",
                                    }}
                                >
                                    {companion?.kind === "rtl_major_change"
                                        ? "تغيّر كبير في Legacy RTL"
                                        : companion?.kind === "new_pulse"
                                          ? "تقرير نبض جديد"
                                          : companion?.kind === "smart_popup"
                                            ? "ملاحظة سوقية"
                                            : "مراجعة المحفظة"}
                                </p>
                            </div>
                        </div>
                        <div style={{ padding: 16 }}>
                            <p
                                style={{
                                    margin: 0,
                                    fontSize: 13,
                                    lineHeight: 1.55,
                                    whiteSpace: "pre-wrap",
                                    color: "var(--txt)",
                                    direction: "rtl",
                                    textAlign: "right",
                                }}
                            >
                                {message}
                            </p>
                            {highlights.length > 0 ? (
                                <div
                                    style={{
                                        display: "flex",
                                        flexWrap: "wrap",
                                        gap: 6,
                                        marginTop: 12,
                                    }}
                                >
                                    {highlights.slice(0, 4).map((h, i) => (
                                        <button
                                            key={`${h.symbol}-${i}`}
                                            type="button"
                                            onClick={() =>
                                                spotlightSymbol(
                                                    h.symbol,
                                                    h.headline_ar,
                                                )
                                            }
                                            style={{
                                                fontSize: 12,
                                                padding: "6px 10px",
                                                borderRadius: 999,
                                                border: `1px solid ${colors.primary}`,
                                                background: `${colors.primary}18`,
                                                color: colors.text,
                                                cursor: "pointer",
                                            }}
                                        >
                                            📍 {h.symbol}
                                        </button>
                                    ))}
                                </div>
                            ) : null}
                            <div
                                style={{
                                    display: "flex",
                                    flexWrap: "wrap",
                                    gap: 8,
                                    marginTop: 16,
                                }}
                            >
                                <button
                                    type="button"
                                    onClick={openExpanded}
                                    style={{
                                        flex: 1,
                                        minWidth: 120,
                                        padding: "10px 12px",
                                        borderRadius: 8,
                                        border: "none",
                                        cursor: "pointer",
                                        fontWeight: 600,
                                        backgroundColor: colors.primary,
                                        color: colors.textInverse,
                                    }}
                                >
                                    {companionCopyAr ? "تحدث معي" : "Talk to me"}
                                </button>
                                <button
                                    type="button"
                                    onClick={goElitePulse}
                                    style={{
                                        flex: 1,
                                        minWidth: 120,
                                        padding: "10px 12px",
                                        borderRadius: 8,
                                        cursor: "pointer",
                                        backgroundColor: "var(--elev2)",
                                        color: "var(--txt)",
                                        border: "1px solid var(--border)",
                                    }}
                                >
                                    {companionCopyAr ? "عرض التقرير" : "View report"}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            ) : null}

            {!chatOpen && isDesignSystemOn() ? (
                // Design System preview (shell artboard): Companion is the primary lg button at bottom inline-end.
                <button
                    type="button"
                    onClick={openChat}
                    data-tour="mmec-companion"
                    title={ar ? "فتح المرافق" : "Open companion"}
                    aria-label={ar ? "فتح مرافق MMEC" : "Open MMEC companion"}
                    className="mmec-btn mmec-btn--primary mmec-btn--lg"
                    style={{
                        position: "fixed",
                        bottom: "calc(var(--mmec-bottom-offset, 0px) + 16px)",
                        // inline-end: bottom-left in Arabic, where the rail is on the right
                        ...(ar ? { left: 16 } : { right: 16 }),
                        zIndex: 10050,
                        boxShadow: "var(--shadow-panel)",
                        fontFamily: ar ? "var(--font-arabic)" : "var(--font-sans)",
                    }}
                >
                    {ar ? "مرافق MMEC" : "Companion"}
                </button>
            ) : !chatOpen ? (
                <button
                    type="button"
                    onClick={openChat}
                    data-tour="mmec-companion"
                    title={ar ? "فتح المرافق" : "Open companion"}
                    aria-label={ar ? "فتح مرافق MMEC" : "Open MMEC companion"}
                    style={{
                        position: "fixed",
                        // Above the Simple-mode tab bar (the shell publishes its height).
                        bottom: "calc(var(--mmec-bottom-offset, 0px) + 20px)",
                        right: 20,
                        zIndex: 10050,
                        display: "flex",
                        alignItems: "center",
                        gap: 10,
                        padding: "8px 14px 8px 8px",
                        borderRadius: 999,
                        border: `2px solid ${colors.primary}`,
                        backgroundColor: "var(--elev)",
                        boxShadow: "var(--lift-3)",
                        cursor: "pointer",
                    }}
                >
                    <CompanionAvatar size={40} pulse />
                    <span
                        style={{
                            fontSize: 13,
                            fontWeight: 600,
                            color: "var(--txt)",
                            maxWidth: 140,
                        }}
                    >
                        {ar ? "مرافق MMEC" : "Companion"}
                    </span>
                </button>
            ) : (
                <div
                    style={{
                        position: "fixed",
                        bottom: isMobile ? 0 : 16,
                        right: isMobile ? 0 : 16,
                        zIndex: 10050,
                        width: isMobile
                            ? "100vw"
                            : "min(520px, calc(100vw - 24px))",
                        height: isMobile
                            ? "min(65vh, calc(100vh - 44px))"
                            : "min(780px, calc(100vh - 32px))",
                        maxHeight: "calc(100vh - 32px)",
                        display: "flex",
                        flexDirection: "column",
                        borderRadius: isMobile ? "12px 12px 0 0" : 12,
                        overflow: "hidden",
                        border: "1px solid var(--border)",
                        boxShadow: "var(--lift-3)",
                        backgroundColor: "var(--elev)",
                    }}
                >
                    <div
                        style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 10,
                            padding: "10px 12px",
                            flexShrink: 0,
                            borderBottom: "1px solid var(--hair)",
                            background: `linear-gradient(90deg, ${colors.primary}18, transparent)`,
                        }}
                    >
                        <CompanionAvatar size={36} />
                        <div style={{ flex: 1, minWidth: 0 }}>
                            <div
                                style={{
                                    fontSize: 14,
                                    fontWeight: 600,
                                    color: "var(--txt)",
                                }}
                            >
                                {ar ? "مرافق MMEC" : "MMEC Companion"}
                            </div>
                            <div
                                style={{
                                    fontSize: 11,
                                    color: "var(--muted)",
                                }}
                            >
                                {ar
                                    ? "اسأل · أبرز على الشاشة"
                                    : "Ask · spotlight on screen"}
                            </div>
                        </div>
                        <button
                            type="button"
                            onClick={togglePopups}
                            title={
                                popupsEnabled
                                    ? ar
                                        ? "إيقاف النوافذ المنبثقة"
                                        : "Turn popups off"
                                    : ar
                                      ? "تشغيل النوافذ المنبثقة"
                                      : "Turn popups on"
                            }
                            style={{
                                fontSize: 11,
                                padding: "6px 10px",
                                borderRadius: 6,
                                border: `1px solid ${popupsEnabled ? colors.primary : colors.surfaceBorder}`,
                                background: popupsEnabled
                                    ? `${colors.primary}22`
                                    : "transparent",
                                color: popupsEnabled ? colors.primary : colors.textMuted,
                                cursor: "pointer",
                                fontWeight: 600,
                                whiteSpace: "nowrap",
                            }}
                        >
                            {popupsEnabled
                                ? ar
                                    ? "🔔 منبثقة"
                                    : "🔔 Popups"
                                : ar
                                  ? "🔕 منبثقة"
                                  : "🔕 Popups off"}
                        </button>
                        <button
                            type="button"
                            onClick={() => {
                                try {
                                    sessionStorage.setItem(
                                        "mmec-platform-tour-autostart",
                                        "1",
                                    );
                                    sessionStorage.removeItem(
                                        "mmec-platform-tour-completed",
                                    );
                                } catch {
                                    /* ignore */
                                }
                                startTour();
                            }}
                            style={{
                                fontSize: 11,
                                padding: "6px 10px",
                                borderRadius: 6,
                                border: `1px solid ${colors.primary}`,
                                background: `${colors.primary}22`,
                                color: colors.primary,
                                cursor: "pointer",
                                fontWeight: 600,
                            }}
                        >
                            {ar
                                ? isTourActive
                                    ? "الجولة…"
                                    : "جولة المنصة"
                                : isTourActive
                                  ? "Tour…"
                                  : "Platform tour"}
                        </button>
                        <button
                            type="button"
                            onClick={closeChat}
                            style={{
                                fontSize: isMobile ? 13 : 11,
                                padding: isMobile ? "8px 16px" : "6px 10px",
                                borderRadius: 6,
                                border: "1px solid var(--border)",
                                background: "transparent",
                                color: "var(--txt-2)",
                                cursor: "pointer",
                                fontWeight: isMobile ? 600 : undefined,
                            }}
                        >
                            {ar ? "إغلاق" : "Close"}
                        </button>
                    </div>

                    {message ? (
                        <div
                            style={{
                                flexShrink: 0,
                                padding: "8px 12px",
                                fontSize: 12.5,
                                lineHeight: 1.45,
                                color: "var(--txt-2)",
                                borderBottom: "1px solid var(--hair)",
                                direction: "rtl",
                                textAlign: "right",
                                maxHeight: 96,
                                overflow: "auto",
                            }}
                        >
                            {message}
                        </div>
                    ) : null}

                    {highlights.length > 0 ? (
                        <div
                            style={{
                                flexShrink: 0,
                                display: "flex",
                                flexWrap: "wrap",
                                gap: 6,
                                padding: "8px 12px",
                                borderBottom: "1px solid var(--hair)",
                            }}
                        >
                            <span
                                style={{
                                    fontSize: 10.5,
                                    fontWeight: 600,
                                    textTransform: "uppercase",
                                    letterSpacing: ".08em",
                                    color: "var(--muted-2)",
                                    width: "100%",
                                    marginBottom: 2,
                                }}
                            >
                                {ar
                                    ? "أبرز على الشاشة:"
                                    : "Spotlight on screen:"}
                            </span>
                            {highlights.slice(0, 6).map((h, i) => (
                                <button
                                    key={`chip-${h.symbol}-${i}`}
                                    type="button"
                                    onClick={() =>
                                        spotlightSymbol(h.symbol, h.headline_ar)
                                    }
                                    style={{
                                        fontSize: 11,
                                        padding: "4px 10px",
                                        borderRadius: 999,
                                        border: `1px solid ${colors.primary}66`,
                                        background: `${colors.primary}14`,
                                        color: colors.text,
                                        cursor: "pointer",
                                    }}
                                >
                                    📍 {h.symbol}
                                </button>
                            ))}
                        </div>
                    ) : null}

                    <div
                        style={{
                            flex: 1,
                            minHeight: 0,
                            display: "flex",
                            flexDirection: "column",
                            overflow: "hidden",
                        }}
                    >
                        <AIChatWidget
                            embedded
                            isCollapsed={false}
                            onToggle={closeChat}
                            marketContext={{
                                ...marketContext,
                                companion_mode: true,
                                chat_mode: "strategy_trading_calls",
                                companion_context: {
                                    message_ar: companion?.message_ar,
                                    message_en: companion?.message_en,
                                    highlights: companion?.highlights,
                                    suggested_calls: companion?.suggested_calls,
                                    active_calls: serverState?.active_calls,
                                    kind: companion?.kind,
                                },
                            }}
                        />
                    </div>
                </div>
            )}
        </>
    );
};

function CompanionAvatar({ size, pulse }: { size: number; pulse?: boolean }) {
    return (
        <div
            style={{
                width: size,
                height: size,
                borderRadius: "50%",
                flexShrink: 0,
                background: "linear-gradient(145deg, #1E40AF, #F4A261)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                boxShadow: pulse
                    ? "0 0 0 4px rgba(30,64,175,0.25)"
                    : "var(--lift-1)",
                animation: pulse
                    ? "mmecCompanionPulse 2s ease-in-out infinite"
                    : undefined,
            }}
        >
            <span style={{ fontSize: size * 0.45, lineHeight: 1 }} aria-hidden>
                ◉
            </span>
            <style>{`
        @keyframes mmecCompanionPulse {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.06); }
        }
      `}</style>
        </div>
    );
}

export default MMECCompanion;
